// ⚠ GENERATED FILE — DO NOT EDIT.
//
// Source of truth: eventsmagic-api `src/boundary/appFeatures.ts`.
// Regenerate with `npm run gen:feature-codes` in eventsmagic-api.
//
// The VALUES for these codes live on the plan in ms_auth (`app_plan_features`,
// space-scoped so each brand has its own catalog). This file is only the vocabulary.
// Design and reasoning: eventsmagic-api `docs/MONETIZATION.md`.
//
// ⚠ There is deliberately NO max_events_qty and NO max_guests_qty — events and guests are
// UNLIMITED on every plan in both brands (MONETIZATION.md R7). We limit by KIND, not by
// length. Do not add them back here; add them to the source or not at all.

export const GENERATED_FEATURE_CODES = {
  /** Account users. Crew on one event don't use a seat. */
  MAX_USERS_QTY: "max_users_qty",
  /** Space for photos, videos and files you upload. */
  STORAGE_MB: "storage_mb",
  /** Share of each sale we keep. */
  SALE_PERCENTAGE: "sale_percentage",
  /** The least we take on any one order. */
  SALE_MIN_CAP: "sale_min_cap",
  /** The most we take on any one order. */
  SALE_MAX_CAP: "sale_max_cap",
  /** Ticket types, pricing, add-ons, discounts, box office. */
  TICKETING: "ticketing",
  /** Our fee comes back when you refund a guest. */
  SALE_FEE_REFUNDABLE: "sale_fee_refundable",
  /** People on the guest list. Paid ticket holders do not count. */
  MAX_GUESTS_QTY: "max_guests_qty",
  /** Co-hosts, organizers and staff per event. */
  MAX_CREW_QTY: "max_crew_qty",
  /** Your own questions on the registration form. */
  RSVP_QUESTIONS_QTY: "rsvp_questions_qty",
  /** Polls, signup lists, prize draws, games, guest book. */
  ENGAGEMENT: "engagement",
  /** Every guest in a party named, each with their own answers. */
  GUEST_LIST: "guest_list",
  /** Waivers and policies guests sign, and guest QR codes. */
  PRO_REGISTRATION: "pro_registration",
  /** Emails you can send and receive each month. */
  EMAIL_QTY: "email_qty",
  /** Texts and WhatsApp per month. Far places cost more. */
  MESSAGE_QTY: "message_qty",
  /** Scheduled reminders you can set up for one event. */
  REMINDERS_PER_EVENT_QTY: "reminders_per_event_qty",
  /** Weekly email cap, lifted once you verify your account. */
  EMAIL_WEEKLY_QTY: "email_weekly_qty",
  /** Weekly message cap, lifted once you verify. */
  MESSAGE_WEEKLY_QTY: "message_weekly_qty",
  /** Reach guests by text and WhatsApp, not only email. */
  RICH_CHANNELS: "rich_channels",
  /** Send through your own SMTP or Twilio account. */
  BYO_SENDER: "byo_sender",
  /** AI assists and background removals each month. */
  AI_QTY: "ai_qty",
  /** Use a video or audio cover, not only an image. */
  RICH_MEDIA: "rich_media",
  /** Recurring events, an event email address and replies to guests. */
  AUTOMATION: "automation",
  /** Budget, rentals, inventory, volunteers, travel, map. */
  PRODUCTION: "production",
  /** Reports, P&L, exports, printing, name badges. */
  INSIGHTS: "insights",
  /** Vouchers, early-bird and price tiers. */
  PROMOTIONS: "promotions",
  /** Artists, sponsors, packages, referrals, memberships, donations. */
  COMMERCIAL: "commercial",
  /** Your logo and name, not ours, in guest emails, passes and pages. */
  BRANDING: "branding",
  /** No platform logo on your event page or guest emails. */
  PLATFORM_BADGE_OFF: "platform_badge_off",
  /** Guest emails show your name as the sender. */
  HOST_NAMED_SENDER: "host_named_sender",
  /** Percent off an upgrade bought in the first hour. */
  UPGRADE_OFFER_PCT: "upgrade_offer_pct",
  /** Your organizer page and its events can be in Google and Bing. */
  SEARCH_INDEXING: "search_indexing",
  /** Your own themes, backgrounds, block colours and custom CSS. */
  SITE_STYLING: "site_styling",
  /** Purchased email top-up. Never expires. */
  EMAIL_CREDITS: "email_credits",
  /** Purchased message top-up. Never expires. */
  MESSAGE_CREDITS: "message_credits",
  /** Purchased AI top-up. Never expires. */
  AI_CREDITS: "ai_credits",
  /** A short call showing you how it all works. */
  QUICK_START_CALL: "quick_start_call",
  /** We build your event from what you describe. */
  EVENT_SETUP: "event_setup",
  /** We build your event and enter your guest list. */
  CONCIERGE_HOURS: "concierge_hours",
  /** One event, upgraded to a plan. */
  EVENT_UPGRADE: "event_upgrade",
} as const;

export type FeatureCode = (typeof GENERATED_FEATURE_CODES)[keyof typeof GENERATED_FEATURE_CODES];

export const GENERATED_PLAN_CODES = {
  FREE: "free",
  PRO: "pro",
  BUSINESS: "business",
} as const;

export type PlanCode = (typeof GENERATED_PLAN_CODES)[keyof typeof GENERATED_PLAN_CODES];

/**
 * The canonical row order for the comparison table, straight from the declaration order in
 * the source of truth (which is also what becomes `app_features.order_no`).
 *
 * ⚠ The front end sorts by THIS, not by `orderNo` from the API — `orderNo` is not
 * exposed on the AppFeature SDL type, and adding it would mean a subgraph change plus a
 * gateway recompose for something we already know at build time. If it is ever added to
 * the SDL, prefer it and delete this.
 */
export const FEATURE_ORDER: readonly string[] = [
  "max_users_qty",
  "storage_mb",
  "sale_percentage",
  "sale_min_cap",
  "sale_max_cap",
  "ticketing",
  "sale_fee_refundable",
  "max_guests_qty",
  "max_crew_qty",
  "rsvp_questions_qty",
  "engagement",
  "guest_list",
  "pro_registration",
  "email_qty",
  "message_qty",
  "reminders_per_event_qty",
  "email_weekly_qty",
  "message_weekly_qty",
  "rich_channels",
  "byo_sender",
  "ai_qty",
  "rich_media",
  "automation",
  "production",
  "insights",
  "promotions",
  "commercial",
  "branding",
  "platform_badge_off",
  "host_named_sender",
  "upgrade_offer_pct",
  "search_indexing",
  "site_styling",
  "email_credits",
  "message_credits",
  "ai_credits",
  "quick_start_call",
  "event_setup",
  "concierge_hours",
  "event_upgrade",
];

export type FeatureGroup = "plan" | "selling" | "guests" | "messaging" | "tools";

/** Pricing-table sections, in display order. */
export const FEATURE_GROUP_ORDER: readonly FeatureGroup[] = [
  "plan",
  "selling",
  "guests",
  "messaging",
  "tools",
] as const;

export const FEATURE_GROUP_LABELS: Record<FeatureGroup, string> = {
  plan: "Your workspace",
  selling: "Selling tickets",
  guests: "Guests & registration",
  messaging: "Messaging",
  tools: "Event tools",
};

export interface FeatureDisplay {
  group?: FeatureGroup;
  /** How to format the raw value for a customer. */
  format?: "number" | "percent" | "currency" | "storage" | "boolean";
  /**
   * ⚠ What a literal 0 means — and it DIFFERS PER FEATURE. 0 reminders means UNLIMITED;
   * 0 messages means NONE. Never infer this from the value; always read it from here.
   */
  zeroMeans?: "unlimited" | "none";
  /** Which direction is better, for values where smaller wins (fees, caps). */
  betterWhen?: "higher" | "lower";
  /** Internal plumbing — keep out of the customer-facing comparison table. */
  hidden?: boolean;
}

/**
 * How each feature should be RENDERED. Keyed by feature code.
 *
 * ⚠ This exists because raw plan values lie on a pricing page: "Reminders per event —
 * Free 1, Pro 0" reads as the paid plan giving you less, and "Fee cap — Free 0" reads as
 * the best deal when 0 actually means NO CAP, the worst. None of that is inferable from
 * the number, so it travels with the code instead.
 */
export const FEATURE_DISPLAY: Record<string, FeatureDisplay> = {
  "max_users_qty": {"group":"plan","format":"number"},
  "storage_mb": {"group":"plan","format":"storage"},
  "sale_percentage": {"group":"selling","format":"percent","betterWhen":"lower"},
  "sale_min_cap": {"group":"selling","format":"currency","betterWhen":"lower"},
  "sale_max_cap": {"group":"selling","format":"currency","zeroMeans":"none","betterWhen":"lower"},
  "ticketing": {"group":"selling","format":"boolean"},
  "sale_fee_refundable": {"group":"selling","format":"boolean"},
  "max_guests_qty": {"group":"guests","format":"number","zeroMeans":"unlimited"},
  "max_crew_qty": {"group":"guests","format":"number","zeroMeans":"unlimited"},
  "rsvp_questions_qty": {"group":"guests","format":"number","zeroMeans":"unlimited"},
  "engagement": {"group":"guests","format":"boolean"},
  "guest_list": {"group":"guests","format":"boolean"},
  "pro_registration": {"group":"guests","format":"boolean"},
  "email_qty": {"group":"messaging","format":"number","zeroMeans":"none"},
  "message_qty": {"group":"messaging","format":"number","zeroMeans":"none"},
  "reminders_per_event_qty": {"group":"messaging","format":"number","zeroMeans":"unlimited"},
  "email_weekly_qty": {"group":"messaging","format":"number","zeroMeans":"none"},
  "message_weekly_qty": {"group":"messaging","format":"number","zeroMeans":"none"},
  "rich_channels": {"group":"messaging","format":"boolean"},
  "byo_sender": {"group":"messaging","format":"boolean"},
  "ai_qty": {"group":"tools","format":"number","zeroMeans":"none","hidden":true},
  "rich_media": {"group":"tools","format":"boolean"},
  "automation": {"group":"tools","format":"boolean"},
  "production": {"group":"tools","format":"boolean"},
  "insights": {"group":"tools","format":"boolean"},
  "promotions": {"group":"tools","format":"boolean"},
  "commercial": {"group":"tools","format":"boolean"},
  "branding": {"group":"tools","format":"boolean"},
  "platform_badge_off": {"group":"tools","format":"boolean"},
  "host_named_sender": {"group":"messaging","format":"boolean"},
  "upgrade_offer_pct": {"group":"plan","format":"number","zeroMeans":"none","hidden":true},
  "search_indexing": {"group":"tools","format":"boolean"},
  "site_styling": {"group":"tools","format":"boolean"},
  "email_credits": {"hidden":true},
  "message_credits": {"hidden":true},
  "ai_credits": {"hidden":true},
  "quick_start_call": {"hidden":true},
  "event_setup": {"hidden":true},
  "concierge_hours": {"hidden":true},
  "event_upgrade": {"hidden":true},
};

/**
 * The DECLARED value type per feature — the same `app_features.feature_value_type` that
 * provisioning writes to ms_auth.
 *
 * ⚠ Use this, not `FEATURE_DISPLAY[code].format`, to decide whether a feature is an on/off
 * capability or a numeric ceiling. The app's gate must branch exactly as the server's
 * `planFeatureGate` does (`isBooleanFeature`), and `format` is a rendering hint that can
 * legitimately differ from the type (a numeric shown as a percentage, a value with no
 * display entry at all).
 */
export const FEATURE_VALUE_TYPES: Record<string, string> = {
  "max_users_qty": "integer",
  "storage_mb": "integer",
  "sale_percentage": "number",
  "sale_min_cap": "number",
  "sale_max_cap": "number",
  "ticketing": "boolean",
  "sale_fee_refundable": "boolean",
  "max_guests_qty": "integer",
  "max_crew_qty": "integer",
  "rsvp_questions_qty": "integer",
  "engagement": "boolean",
  "guest_list": "boolean",
  "pro_registration": "boolean",
  "email_qty": "integer",
  "message_qty": "integer",
  "reminders_per_event_qty": "integer",
  "email_weekly_qty": "integer",
  "message_weekly_qty": "integer",
  "rich_channels": "boolean",
  "byo_sender": "boolean",
  "ai_qty": "integer",
  "rich_media": "boolean",
  "automation": "boolean",
  "production": "boolean",
  "insights": "boolean",
  "promotions": "boolean",
  "commercial": "boolean",
  "branding": "boolean",
  "platform_badge_off": "boolean",
  "host_named_sender": "boolean",
  "upgrade_offer_pct": "integer",
  "search_indexing": "boolean",
  "site_styling": "boolean",
  "email_credits": "integer",
  "message_credits": "integer",
  "ai_credits": "integer",
  "quick_start_call": "integer",
  "event_setup": "integer",
  "concierge_hours": "integer",
  "event_upgrade": "integer",
};

/** The customer-facing name per feature, for a locked control's copy. */
export const FEATURE_NAMES: Record<string, string> = {
  "max_users_qty": "Team seats",
  "storage_mb": "Storage",
  "sale_percentage": "Service fee",
  "sale_min_cap": "Smallest fee",
  "sale_max_cap": "Fee cap",
  "ticketing": "Ticketing",
  "sale_fee_refundable": "Fee returned on refunds",
  "max_guests_qty": "Guests",
  "max_crew_qty": "Crew",
  "rsvp_questions_qty": "Custom questions",
  "engagement": "Guest engagement",
  "guest_list": "Every guest by name",
  "pro_registration": "Advanced registration",
  "email_qty": "Emails per month",
  "message_qty": "Messages per month",
  "reminders_per_event_qty": "Reminders per event",
  "email_weekly_qty": "Email burst limit",
  "message_weekly_qty": "Message burst limit",
  "rich_channels": "SMS & WhatsApp",
  "byo_sender": "Bring your own sender",
  "ai_qty": "AI actions per month",
  "rich_media": "Video & audio covers",
  "automation": "Automation",
  "production": "Event production",
  "insights": "Analytics & reports",
  "promotions": "Promotions & vouchers",
  "commercial": "Commercial tools",
  "branding": "Branding & white label",
  "platform_badge_off": "No platform logo",
  "host_named_sender": "Emails from your name",
  "upgrade_offer_pct": "Upgrade offer",
  "search_indexing": "Search engine listing",
  "site_styling": "Advanced styling",
  "email_credits": "Extra emails",
  "message_credits": "Extra messages",
  "ai_credits": "Extra AI actions",
  "quick_start_call": "Quick start call",
  "event_setup": "Event setup",
  "concierge_hours": "Event setup + guest list",
  "event_upgrade": "Event upgrade",
};

/**
 * The customer-facing DESCRIPTION per feature — what it gives you, in one line.
 *
 * ⚠ Written for a CUSTOMER, not for us, and capped at 64 characters by the ms_auth column that
 * also stores it. A whole-page lock uses this as its "what you'd get" line, so it is the sentence
 * that has to earn the upgrade.
 */
export const FEATURE_DESCRIPTIONS: Record<string, string> = {
  "max_users_qty": "Account users. Crew on one event don't use a seat.",
  "storage_mb": "Space for photos, videos and files you upload.",
  "sale_percentage": "Share of each sale we keep.",
  "sale_min_cap": "The least we take on any one order.",
  "sale_max_cap": "The most we take on any one order.",
  "ticketing": "Ticket types, pricing, add-ons, discounts, box office.",
  "sale_fee_refundable": "Our fee comes back when you refund a guest.",
  "max_guests_qty": "People on the guest list. Paid ticket holders do not count.",
  "max_crew_qty": "Co-hosts, organizers and staff per event.",
  "rsvp_questions_qty": "Your own questions on the registration form.",
  "engagement": "Polls, signup lists, prize draws, games, guest book.",
  "guest_list": "Every guest in a party named, each with their own answers.",
  "pro_registration": "Waivers and policies guests sign, and guest QR codes.",
  "email_qty": "Emails you can send and receive each month.",
  "message_qty": "Texts and WhatsApp per month. Far places cost more.",
  "reminders_per_event_qty": "Scheduled reminders you can set up for one event.",
  "email_weekly_qty": "Weekly email cap, lifted once you verify your account.",
  "message_weekly_qty": "Weekly message cap, lifted once you verify.",
  "rich_channels": "Reach guests by text and WhatsApp, not only email.",
  "byo_sender": "Send through your own SMTP or Twilio account.",
  "ai_qty": "AI assists and background removals each month.",
  "rich_media": "Use a video or audio cover, not only an image.",
  "automation": "Recurring events, an event email address and replies to guests.",
  "production": "Budget, rentals, inventory, volunteers, travel, map.",
  "insights": "Reports, P&L, exports, printing, name badges.",
  "promotions": "Vouchers, early-bird and price tiers.",
  "commercial": "Artists, sponsors, packages, referrals, memberships, donations.",
  "branding": "Your logo and name, not ours, in guest emails, passes and pages.",
  "platform_badge_off": "No platform logo on your event page or guest emails.",
  "host_named_sender": "Guest emails show your name as the sender.",
  "upgrade_offer_pct": "Percent off an upgrade bought in the first hour.",
  "search_indexing": "Your organizer page and its events can be in Google and Bing.",
  "site_styling": "Your own themes, backgrounds, block colours and custom CSS.",
  "email_credits": "Purchased email top-up. Never expires.",
  "message_credits": "Purchased message top-up. Never expires.",
  "ai_credits": "Purchased AI top-up. Never expires.",
  "quick_start_call": "A short call showing you how it all works.",
  "event_setup": "We build your event from what you describe.",
  "concierge_hours": "We build your event and enter your guest list.",
  "event_upgrade": "One event, upgraded to a plan.",
};

/**
 * The METERED features — feature code → the period its allowance resets on.
 *
 * ⚠ A feature is in here IF AND ONLY IF it is counted in ms_auth's `app_feature_usage`, which
 * is what `appFeatureUsageSummary` reports on. That makes this the list a usage display may
 * iterate: everything else on a plan is a ceiling or a capability with no consumption to show.
 *
 * ⚠ Do NOT read a limit from the usage summary. It carries its own `limitValue` with a
 * DIFFERENT sentinel convention (ms_auth uses `-1` for unlimited, and falls back to `0` when a
 * plan row is missing), whereas this app reads `0` through the per-feature `zeroMeans`
 * declaration in `FEATURE_DISPLAY`. Two conventions for one number is how a billing page and
 * the gate that enforces it end up disagreeing. Take `usageCount` from the summary and the
 * LIMIT from the plan, which is the same authority the gate uses.
 */
export const FEATURE_PERIODS: Record<string, string> = {
  "email_qty": "month",
  "message_qty": "month",
  "ai_qty": "month",
};

/**
 * Metered allowance code → the purchasable CREDIT feature that tops it up.
 *
 * ⚠ Credits and allowances are DIFFERENT FEATURES with different codes — `email_qty` is the
 * monthly allowance, `email_credits` is the wallet you buy. They are counted in different
 * tables (`app_feature_usage` vs `app_feature_credit`) and behave differently: the allowance
 * resets every month, credits never expire and are spent FIRST.
 *
 * ⚠ Do NOT derive this from the code strings. `email_qty` → `email_credits` happens to be a
 * substitution today; it is a declaration, not a spelling rule, and the source asserts that
 * each key is genuinely metered and each value genuinely purchasable.
 *
 * ⚠ An allowance ABSENT from this map cannot be topped up at all. Do not offer to sell one.
 */
export const FEATURE_TOP_UPS: Record<string, string> = {
  "email_qty": "email_credits",
  "message_qty": "message_credits",
  "ai_qty": "ai_credits",
};

/**
 * The hand-delivered SERVICES this space sells — a call, a setup, concierge hours.
 *
 * ⚠ A SERVICE IS NOT A PLAN FEATURE. It is bought, granted, refunded and discounted by exactly
 * the same code as a credit bundle (same wallet, same purchase rail), and the ONLY thing that
 * tells them apart is `addonType: 'SERVICE'` on the ms_auth row. So this list is what the
 * services catalog selects ON, and what anything describing "what your plan includes" must
 * filter OUT. The generator refuses to emit a service that is not `display.hidden`.
 *
 * ⚠ Source of truth: eventsmagic-api `src/boundary/appFeatures.ts`. Regenerate; never edit.
 */
export const FEATURE_SERVICES: string[] = [
  "quick_start_call",
  "event_setup",
  "concierge_hours",
];

/** True when a feature code is a hand-delivered service rather than a plan inclusion. */
export const isServiceFeature = (code: string): boolean => FEATURE_SERVICES.includes(code);

/** How the app gates a page for a feature. See `FEATURE_GATE_KINDS` below. */
export type FeatureGateKind = "page" | "add" | "none";

export const FEATURE_GATE_KIND_VALUES: readonly FeatureGateKind[] = [
  "page",
  "add",
  "none",
] as const;

/**
 * ⭐ WHICH KIND OF GATE EACH FEATURE GETS — the declaration that makes plan gating a system
 * rather than a per-page judgement call (ROADMAP A2.10).
 *
 *   "page" — the whole page becomes the upgrade screen (`PremiumFeatureGate`). ⚠ Correct ONLY
 *            where an account without the feature can never have accumulated anything to
 *            preserve — that is the single case in which a page lock cannot hide data.
 *   "add"  — the page works; only "add another" is refused (`PlanFeatureNotice`).
 *   "none" — never gate a page on this: internal plumbing, wallets, services, and capabilities
 *            whose surface does not exist yet.
 *
 * ⚠ DO NOT READ A MISSING ENTRY AS "none". An unknown code has not been DECLARED, which is a
 * different fact from having been declared ungated — `featureGateKind()` in
 * `@/utils/planGating` answers with the safe default instead, and says why there.
 *
 * ⚠ Source of truth: eventsmagic-api `src/boundary/appFeatures.ts`. Regenerate; never edit.
 */
export const FEATURE_GATE_KINDS: Record<string, FeatureGateKind> = {
  "max_users_qty": "page",
  "storage_mb": "none",
  "sale_percentage": "none",
  "sale_min_cap": "none",
  "sale_max_cap": "none",
  "ticketing": "page",
  "sale_fee_refundable": "none",
  "max_guests_qty": "add",
  "max_crew_qty": "add",
  "rsvp_questions_qty": "add",
  "engagement": "page",
  "guest_list": "add",
  "pro_registration": "page",
  "email_qty": "add",
  "message_qty": "add",
  "reminders_per_event_qty": "add",
  "email_weekly_qty": "none",
  "message_weekly_qty": "none",
  "rich_channels": "add",
  "byo_sender": "none",
  "ai_qty": "add",
  "rich_media": "add",
  "automation": "add",
  "production": "page",
  "insights": "page",
  "promotions": "page",
  "commercial": "page",
  "branding": "page",
  "platform_badge_off": "none",
  "host_named_sender": "none",
  "upgrade_offer_pct": "none",
  "search_indexing": "none",
  "site_styling": "add",
  "email_credits": "none",
  "message_credits": "none",
  "ai_credits": "none",
  "quick_start_call": "none",
  "event_setup": "none",
  "concierge_hours": "none",
  "event_upgrade": "none",
};

/**
 * What a code with no declaration gets.
 *
 * ⚠ The safe direction, on purpose: an undeclared feature someone wires a gate to gets the
 * add-only notice, which at worst under-gates — the page works and the save is refused with
 * the server's own sentence. The opposite default would put an upgrade screen over a working
 * page the first time anyone added a feature in a hurry.
 */
export const DEFAULT_FEATURE_GATE: FeatureGateKind = "add";
